import PropTypes from 'prop-types';
import React, { useMemo } from 'react';
import { formatDateTime } from '../../../library/utilities/intl.js';
import SmallSection from '../../design/small-section.jsx';
import { DocumentsDownloader } from '../../form/index.js';

const FilePropType = PropTypes.shape({
    url: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    comment: PropTypes.string,
    uploaded_by: PropTypes.string,
    uploaded_time: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
});

const FilesPropType = PropTypes.oneOfType([
    PropTypes.arrayOf(FilePropType),
    FilePropType,
    PropTypes.string,
]);

const SECTION_HEADER_STYLE = {
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'space-between',
};

const SECTION_GRID_STYLE = {
    display: 'grid',
    gap: '0.75rem',
    gridTemplateColumns: 'repeat(3, 1fr)',
};

const FILE_CONTAINER_STYLE = {
    background: '#ebf2fc',
    padding: '0.75rem',
    borderRadius: '6px',
};

const getFileName = (url) => url?.split('/').pop() || url;

const normalizeFiles = (files) => {
    if (!files) return [];

    if (Array.isArray(files)) {
        return files.flatMap(normalizeFiles);
    }

    if (typeof files === 'string') {
        const trimmedFiles = files.trim();
        if (!trimmedFiles) return [];

        try {
            return normalizeFiles(JSON.parse(trimmedFiles));
        } catch {
            return [{ url: trimmedFiles, name: getFileName(trimmedFiles) }];
        }
    }

    if (typeof files === 'object' && files.url) {
        return [{
            ...files,
            name: files.name || getFileName(files.url),
        }];
    }

    return [];
};

const ProjectDocumentSection = ({ projectId, title, files, dateTime, comment }) => {
    const normalizedFiles = normalizeFiles(files).filter(file => !!file.url);
    const normalizedComment = typeof comment === 'string' ? comment.trim() : '';

    if (normalizedFiles.length === 0) {
        return null;
    }

    return (
        <SmallSection
            containerStyles={{ margin: '0.25rem 0 0.5rem 0' }}
            bodyStyle={{ padding: '0.65rem' }}
            header={(
                <div style={SECTION_HEADER_STYLE}>
                    <h3 style={{ margin: 0, fontSize: '0.95rem' }}>{title}</h3>
                    {!!dateTime && <span style={{ color: '#595959', fontSize: '0.8rem' }}>{formatDateTime(dateTime)}</span>}
                </div>
            )}
        >
            <div style={SECTION_GRID_STYLE}>
                {normalizedFiles.map(file => (
                    <DocumentsDownloader
                        key={file.url}
                        image={file.url}
                        name={file.name}
                        comment={file.comment}
                        project_id={projectId}
                        containerStyles={FILE_CONTAINER_STYLE}
                        uploadedAt={file.uploaded_time ?? dateTime}
                    />
                ))}
            </div>
            {normalizedComment.length > 0 && (
                <>
                    <hr style={{ margin: '0.5rem 0' }} />
                    <strong>Comment:</strong> <i>{normalizedComment}</i>
                </>
            )}
        </SmallSection>
    );
};

ProjectDocumentSection.propTypes = {
    projectId: PropTypes.number.isRequired,
    title: PropTypes.string.isRequired,
    files: FilesPropType,
    dateTime: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.instanceOf(Date)]),
    comment: PropTypes.string,
};

const parseTimestamp = (dateVal) => {
    if (!dateVal) return 0;
    if (typeof dateVal === 'number') return dateVal;
    const parsed = new Date(dateVal).getTime();
    return isNaN(parsed) ? 0 : parsed;
};

const ProjectDocuments = ({ projectId, documents = {} }) => {
    const sections = useMemo(() => {
        const result = [];
        const {
            project_documents = [],
            client_quotations = [],
            item_documents = [],
            additional_quotations = [],
        } = documents?.data || documents || {};

        // 1. Client Quotations (Sent and Approved grouped in the same row)
        client_quotations.forEach((quotation) => {
            const quotationFiles = [];
            if (quotation.pdf?.approved_quotation) {
                quotationFiles.push({
                    url: quotation.pdf.approved_quotation,
                    name: `Approved Quotation (${getFileName(quotation.pdf.approved_quotation)})`,
                    // Approval is the last change to the quotation row
                    uploaded_time: quotation.approved_at || quotation.updatedAt,
                });
            }
            if (quotation.pdf?.quotation) {
                quotationFiles.push({
                    url: quotation.pdf.quotation,
                    name: `Sent Quotation (${getFileName(quotation.pdf.quotation)})`,
                    // The quotation row is created when the quotation is sent
                    uploaded_time: quotation.createdAt,
                    comment: quotation.version ? `Quotation Version: ${quotation.version}` : undefined,
                });
            }

            if (quotationFiles.length > 0) {
                result.push({
                    id: `quotation_${quotation.id}`,
                    title: 'Quotation',
                    files: quotationFiles,
                    dateTime: quotation.approved_at || quotation.updatedAt || quotation.createdAt,
                    timestamp: parseTimestamp(quotation.approved_at || quotation.updatedAt || quotation.createdAt),
                    comment: quotation.approved_by_user ? `Approved by ${quotation.approved_by_user.first_name} ${quotation.approved_by_user.last_name || ''}`.trim() : undefined,
                });
            }
        });

        // 2. Project Documents (Visuals & Project Images)
        project_documents.forEach((doc) => {
            const contextLabel = doc.context === 'visual'
                ? 'Visuals'
                : doc.context === 'project_images'
                ? 'Project Images'
                : (doc.context || 'Project Document');

            const files = normalizeFiles(doc.documents).filter(f => !!f.url);
            if (files.length > 0) {
                result.push({
                    id: `proj_doc_${doc.id || doc.context}`,
                    title: contextLabel,
                    files: files,
                    dateTime: doc.updatedAt || doc.createdAt,
                    timestamp: parseTimestamp(doc.updatedAt || doc.createdAt),
                });
            }
        });

        // 3. Item Documents (only Artwork Received from Customer)
        item_documents.forEach((doc) => {
            if (doc.type_name !== 'client_artwork') {
                return;
            }
            const itemName = doc.item?.product_name || `Item #${doc.item_id}`;
            const files = normalizeFiles(doc.documents).filter(f => !!f.url);

            if (files.length > 0) {
                result.push({
                    id: `item_doc_${doc.id}`,
                    title: `Artwork Received from Customer — ${itemName}`,
                    files: files,
                    dateTime: doc.updatedAt || doc.createdAt,
                    timestamp: parseTimestamp(doc.updatedAt || doc.createdAt),
                    comment: doc.comments,
                });
            }
        });

        // 4. Additional Quotations (Sent & Approved in the same row)
        additional_quotations.forEach((aq) => {
            const itemName = aq.item?.product_name || `Item #${aq.item_id}`;
            const aqFiles = [];

            if (aq.pdf?.approved_quotation) {
                aqFiles.push({
                    url: aq.pdf.approved_quotation,
                    name: `Approved Additional Quotation (${getFileName(aq.pdf.approved_quotation)})`,
                });
            }
            if (aq.pdf?.quotation) {
                aqFiles.push({
                    url: aq.pdf.quotation,
                    name: `Sent Additional Quotation (${getFileName(aq.pdf.quotation)})`,
                });
            }

            if (aqFiles.length > 0) {
                result.push({
                    id: `aq_${aq.id}`,
                    title: `Additional Quotation — ${itemName}`,
                    files: aqFiles,
                    dateTime: aq.approved_at || aq.updatedAt || aq.createdAt,
                    timestamp: parseTimestamp(aq.approved_at || aq.updatedAt || aq.createdAt),
                });
            }
        });

        // Sort recent first
        result.sort((a, b) => b.timestamp - a.timestamp);

        return result;
    }, [documents]);

    if (sections.length === 0) {
        return (
            <div style={{ padding: '1rem', textAlign: 'center', color: '#8c8c8c', background: '#fafafa', borderRadius: '6px' }}>
                <p style={{ margin: 0 }}>No document files uploaded yet.</p>
            </div>
        );
    }

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem', marginTop: 0 }}>
            {sections.map((section) => (
                <ProjectDocumentSection
                    key={section.id}
                    projectId={projectId}
                    title={section.title}
                    files={section.files}
                    dateTime={section.dateTime}
                    comment={section.comment}
                />
            ))}
        </div>
    );
};

ProjectDocuments.propTypes = {
    projectId: PropTypes.number.isRequired,
    documents: PropTypes.shape({
        project_documents: PropTypes.array,
        client_quotations: PropTypes.array,
        item_documents: PropTypes.array,
        additional_quotations: PropTypes.array,
    }),
};

export default ProjectDocuments;
